import React, { useState, useEffect } from "react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { User, History, Crown, LogOut } from "lucide-react";
import { ThemeToggle } from "@/components/ThemeToggle";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { SettingsDialog } from "@/components/SettingsDialog";
import { useAuth } from "@/hooks/useAuth";
import { useLanguage } from "@/context/LanguageContext";

interface UserProfileDropdownProps {
  userName?: string;
  userInitials?: string;
}
export const UserProfileDropdown: React.FC<UserProfileDropdownProps> = ({
  userName = "Azzam Ali S",
  userInitials = "Az"
}) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useLanguage();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [subscriptionStatus, setSubscriptionStatus] = useState<string | null>("free");

  useEffect(() => {
    if (!user) return;
    
    const fetchSubscription = async () => {
      const { data } = await supabase
        .from("profiles")
        .select("subscription_status")
        .eq("id", user.id)
        .single();
      
      if (data) {
        setSubscriptionStatus(data.subscription_status);
      }
    };
    
    fetchSubscription();
  }, [user]);

  const planLabel = subscriptionStatus === "active" ? t("common.proPlan") : t("common.freePlan");
  
  const handleSignOut = async () => {
    try {
      const {
        error
      } = await supabase.auth.signOut();
      if (error) throw error;
      toast.success("Signed out successfully");
      navigate("/auth/signin");
    } catch (error: any) {
      toast.error(error.message || "Failed to sign out");
    }
  };
  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
        <button className="inline-flex h-fit w-full items-center justify-between rounded-2xl border-2 border-border bg-card p-3 hover:bg-background transition focus:outline-none focus:ring-2 focus:ring-primary/20">
          <div className="flex flex-grow items-center">
            <span className="mr-4 flex h-6 w-6 items-center justify-center overflow-hidden rounded-full border-2 border-border text-xs uppercase">
              <span className="flex h-full w-full items-center justify-center rounded-full bg-muted">
                {userInitials}
              </span>
            </span>
            <div className="flex flex-grow flex-col -ml-1">
              <p className="overflow-hidden text-left">{userName}</p>
            </div>
          </div>
          <svg xmlns="http://www.w3.org/2000/svg" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4">
            <path d="m6 9 6 6 6-6" />
          </svg>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel>
          <div className="flex flex-col space-y-1">
            <p className="text-sm font-medium leading-none">{userName}</p>
            <p className="text-xs leading-none text-muted-foreground">
              {planLabel}
            </p>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => setSettingsOpen(true)}>
          <User className="ltr:mr-2 rtl:ml-2 h-4 w-4" />
          <span>{t("userMenu.account")}</span>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate("/history")}>
          <History className="ltr:mr-2 rtl:ml-2 h-4 w-4" />
          <span>{t("userMenu.history")}</span>
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => navigate("/pricing")} className="text-success hover:bg-success/10 focus:bg-success/10 focus:text-success">
          <Crown className="ltr:mr-2 rtl:ml-2 h-4 w-4" />
          <span>{t("userMenu.pricing")}</span>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={e => e.preventDefault()} className="justify-between">
          <span>{t("userMenu.theme")}</span>
          <ThemeToggle />
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={handleSignOut} className="text-destructive hover:bg-destructive/10 focus:bg-destructive/10 focus:text-destructive">
          <LogOut className="ltr:mr-2 rtl:ml-2 h-4 w-4" />
          <span>{t("userMenu.logout")}</span>
        </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      
      <SettingsDialog open={settingsOpen} onOpenChange={setSettingsOpen} />
    </>
  );
};